import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../App';
import { useT, LOCALE_LABELS, LOCALE_ORDER } from '../i18n';
import { visibleNavItems } from '../navVisibility';
import { useSelfHosted } from '../lib/instanceLicence';
import { Avatar, AvatarFallback } from './ui/avatar';
import { LogOut, User, Settings, Globe, ChevronRight, Bell } from 'lucide-react';
import XpnsrSidebar from './XpnsrSidebar';
import { BACKEND_URL } from '../runtimeConfig';

// API docs are served by the backend (Swagger UI under
// ``/api/public/docs``). The frontend can live on a different host (e.g.
// ``tracker.xpnsr.tech`` while the API runs on ``api-tracker.xpnsr.tech``)
// so the link must be absolute against the resolved backend URL — a bare
// ``/api/public/docs`` resolves against ``window.location`` and lands on
// the SPA's catch-all, rendering an empty page.
//
// We read ``REACT_APP_BACKEND_URL`` directly here instead of importing the
// ``API`` constant from ``App.js``: ``App.js`` imports this Navbar module,
// so a top-level ``import { API } from '../App'`` creates a circular
// dependency. Webpack resolves the cycle by handing back the partially-
// initialised module, which puts ``API`` in the temporal dead zone and
// crashes the entire bundle with ``ReferenceError: Cannot access 'API'
// before initialization`` before React ever mounts.
const RESOLVED_BACKEND_URL = BACKEND_URL;
const API_DOCS_URL = `${RESOLVED_BACKEND_URL}/api/public/docs`;

// Nav items are grouped so 27 routes don't render as one wall of links.
// Group order and labels are consumed by XpnsrSidebar; the group key is
// used to look up the localized heading via t('group.<key>').
const NAV_ITEMS = [
  // Grouped by task (plan stage 7): launch, partners and conversions,
  // statistics, protection. Page addresses are unchanged; search (⌘K) finds all.
  { key: 'Dashboard', href: '/dashboard', group: 'launch' },
  { key: 'Campaigns', href: '/campaigns', group: 'launch' },
  { key: 'Sources', href: '/traffic-sources', group: 'launch' },
  { key: 'Offers', href: '/offers', group: 'launch' },
  { key: 'Landing pages', href: '/landing-pages', group: 'launch' },
  { key: 'Pre-Landings', href: '/pre-landings', group: 'launch' },
  { key: 'White pages', href: '/white-pages', group: 'launch' },
  { key: 'Domains', href: '/domains', group: 'launch' },
  { key: 'Fallback rules', href: '/traffic', group: 'launch' },
  { key: 'A/B Tests', href: '/ab-testing', group: 'launch' },
  { key: 'Automation', href: '/automation', group: 'launch' },
  { key: 'Marketplace', href: '/marketplace', group: 'launch' },

  { key: 'Affiliate networks', href: '/affiliate-networks', group: 'partners' },
  { key: 'Leads', href: '/leads', group: 'partners' },
  { key: 'Integrations', href: '/integrations', group: 'partners' },
  { key: 'Conversion API', href: '/conversion-api', group: 'partners' },
  { key: 'Conversion types', href: '/conversion-types', group: 'partners' },
  { key: 'S2S log', href: '/logs/s2s', group: 'partners' },
  { key: 'CAPI DLQ', href: '/admin/capi-dlq', group: 'partners' },

  { key: 'Analytics', href: '/analytics', group: 'stats' },
  { key: 'Reports', href: '/reports', group: 'stats' },
  { key: 'Sub-ID reports', href: '/reports/sub-ids', group: 'stats' },
  { key: 'Clicks log', href: '/clicks/log', group: 'stats' },
  { key: 'Click traces', href: '/diagnostics/click-traces', group: 'stats' },
  { key: 'Trends', href: '/analytics/trends', group: 'stats' },
  { key: 'Compare', href: '/campaigns/compare', group: 'stats' },
  { key: 'Custom metrics', href: '/custom-metrics', group: 'stats' },
  { key: 'Saved reports', href: '/reports/saved', group: 'stats' },
  { key: 'Scheduled reports', href: '/reports/scheduled', group: 'stats' },

  { key: 'Anti-fraud', href: '/anti-fraud', group: 'protection' },
  { key: 'TLS fingerprints', href: '/anti-fraud/tls', group: 'protection' },
  { key: 'Bot lists', href: '/settings/bot-lists', group: 'protection' },

  { key: 'Workspace', href: '/workspace', group: 'workspace' },
  { key: 'Archive', href: '/archive', group: 'workspace' },
  { key: 'Member access', href: '/workspace/access', group: 'workspace' },
  { key: 'Ad accounts', href: '/ad-account-connections', group: 'workspace' },
  { key: 'Cost entries', href: '/cost-entries', group: 'workspace' },
  { key: 'API keys', href: '/api-keys', group: 'workspace' },
  { key: 'API docs', href: API_DOCS_URL, external: true, group: 'workspace' },
  { key: 'Documentation', href: '/docs', group: 'workspace' },
  { key: 'Security', href: '/settings/security', group: 'workspace' },
  // Per-workspace audit & compliance — client-facing (their own team's
  // actions, their own visitors' data requests), so not operator-gated.
  { key: 'Audit log', href: '/admin/audit', group: 'workspace' },
  { key: 'GDPR / DSAR', href: '/settings/gdpr', group: 'workspace' },
  { key: 'Consent banner', href: '/consent-banner', group: 'workspace' },

  // Operator-only group: rendered exclusively for platform admins
  // (user.is_admin — PLATFORM_ADMIN_EMAILS allowlist or a back-office
  // grant). Clients never see these entries — see visibleNavItems().
  { key: 'Platform admin', href: '/admin/platform', group: 'admin' },
  { key: 'Error reports', href: '/admin/errors', group: 'admin' },
  { key: 'System health', href: '/admin/health', group: 'admin' },
  { key: 'System status', href: '/admin/system', group: 'admin' },

  { key: 'Billing', href: '/billing', group: 'account' },
  { key: 'Self-hosted', href: '/self-hosted', group: 'account' },
  { key: 'Branding', href: '/workspace/branding', group: 'account' },
  { key: 'Notifications', href: '/notifications/preferences', group: 'account' },
  { key: 'Settings', href: '/settings', group: 'account' },
];


export default function Navbar() {
  const { user, logout } = useAuth();
  const { t, locale, setLocale } = useT();
  const [menuOpen, setMenuOpen] = React.useState(false);
  const [langOpen, setLangOpen] = React.useState(false);
  const menuRef = React.useRef(null);

  React.useEffect(() => {
    if (!menuOpen) return;
    const handle = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
        setLangOpen(false);
      }
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [menuOpen]);

  const footer = (
    <div className="xp-account xp-account--compact" ref={menuRef}>
      <button
        type="button"
        onClick={() => { setMenuOpen((prev) => !prev); setLangOpen(false); }}
        className="xp-account-btn"
        data-testid="account-trigger"
      >
        <Avatar className="h-6 w-6 flex-shrink-0">
          <AvatarFallback className="bg-primary/15 text-primary font-display text-[10px]">
            {user?.full_name?.charAt(0) || 'U'}
          </AvatarFallback>
        </Avatar>
        <div className="xp-account-meta">
          <div className="xp-account-name">{user?.full_name || t('User')}</div>
        </div>
        <ChevronRight className={`xp-account-chevron ${menuOpen ? 'is-open' : ''}`} />
      </button>

      {menuOpen && (
        <div className="xp-account-pop">
          <div className="xp-account-pop-h">{t('My account')}</div>
          <div className="xp-account-pop-email">{user?.email}</div>
          <div className="xp-account-pop-sep" />
          <Link
            to="/settings"
            onClick={() => setMenuOpen(false)}
            className="xp-account-pop-item"
          >
            <User className="w-4 h-4" />
            {t('Profile')}
          </Link>
          <Link
            to="/settings"
            onClick={() => setMenuOpen(false)}
            className="xp-account-pop-item"
          >
            <Settings className="w-4 h-4" />
            {t('Settings')}
          </Link>
          <Link
            to="/notifications/preferences"
            onClick={() => setMenuOpen(false)}
            className="xp-account-pop-item"
          >
            <Bell className="w-4 h-4" />
            {t('Notifications')}
          </Link>
          <div className="xp-account-pop-sep" />

          {/* Language picker inside the menu */}
          <button
            type="button"
            className="xp-account-pop-item"
            onClick={() => setLangOpen((v) => !v)}
          >
            <Globe className="w-4 h-4" />
            {t('Language')}
            <span className="xp-account-pop-lang-badge">{locale.toUpperCase()}</span>
          </button>
          {langOpen && (
            <div className="xp-account-pop-lang-list">
              {LOCALE_ORDER.map((code) => (
                <button
                  key={code}
                  type="button"
                  className={`xp-account-pop-lang-item ${locale === code ? 'is-on' : ''}`}
                  onClick={() => { setLocale(code); setLangOpen(false); }}
                  data-testid={`lang-${code}`}
                >
                  {LOCALE_LABELS[code] || code}
                </button>
              ))}
            </div>
          )}
          <div className="xp-account-pop-sep" />
          <button
            type="button"
            onClick={() => { setMenuOpen(false); logout(); }}
            data-testid="logout-button"
            className="xp-account-pop-item xp-account-pop-item-btn"
          >
            <LogOut className="w-4 h-4" />
            {t('Sign out')}
          </button>
        </div>
      )}
    </div>
  );

  const selfHosted = useSelfHosted();
  const authedNav = React.useMemo(
    () => visibleNavItems(NAV_ITEMS, user, { selfHosted }),
    [user, selfHosted]
  );

  return <XpnsrSidebar authedNav={authedNav} footer={footer} />;
}
